import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, CalendarCheck, AlertOctagon, CalendarX, CheckCircle2 } from "lucide-react";
import { StatCard } from "./DashBits";

function FollowupList({ title, icon: Icon, accent, rows, emptyText, testid }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className={`w-4 h-4 ${accent}`} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2 text-xs" data-testid={testid}>
        {rows.length === 0 ? (
          <div className="text-slate-400 p-2">{emptyText}</div>
        ) : (
          <div className="divide-y">
            {rows.map((c) => (
              <Link key={c.id} to={`/leads/${c.id}`}
                    className="flex items-center justify-between px-2 py-2 hover:bg-slate-50"
                    data-testid={`fu-${c.customer_id}`}>
                <div>
                  <div className="font-medium text-slate-800">{c.customer_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{c.customer_id} · {c.assigned_to || "unassigned"}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[11px] text-slate-700">
                    {c.follow_up_at ? new Date(c.follow_up_at).toLocaleString() : "—"}
                  </div>
                  <Badge variant="outline" className="text-[9px] font-mono">{c.disposition}</Badge>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Follow-ups tab — due today / upcoming / overdue / completed / missed. */
export default function FollowupsTab({ data }) {
  const f = data?.followups || {};
  const lists = data?.followup_lists || {};
  const kpis = [
    { label: "Due Today", value: f.due_today ?? 0, icon: CalendarClock, accent: "text-indigo-600" },
    { label: "Upcoming", value: f.upcoming ?? 0, icon: CalendarCheck, accent: "text-sky-600" },
    { label: "Overdue", value: f.overdue ?? 0, icon: AlertOctagon, accent: "text-rose-600" },
    { label: "Completed", value: f.completed ?? 0, icon: CheckCircle2, accent: "text-emerald-600" },
    { label: "Missed (never called back)", value: f.missed ?? 0, icon: CalendarX, accent: "text-amber-600" },
  ];
  return (
    <div className="space-y-4" data-testid="dash-tab-followups">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpis.map((k) => (
          <StatCard key={k.label} {...k}
                    testid={`kpi-fu-${k.label.split(" ")[0].toLowerCase()}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FollowupList title="Overdue Callbacks — action needed" icon={AlertOctagon} accent="text-rose-600"
                      rows={lists.overdue || []} emptyText="Nothing overdue. 🎯" testid="overdue-list" />
        <FollowupList title="Due Today" icon={CalendarClock} accent="text-indigo-600"
                      rows={lists.due_today || []} emptyText="No callbacks due today." testid="due-today-list" />
      </div>
    </div>
  );
}
